import React, { useState, useImperativeHandle, forwardRef } from "react";
import api from "../../../utils/api";

import { FaTrashAlt, FaPlus, FaSync } from "react-icons/fa";
import { empty } from "../../../helper/generalHelper";
import ImageUploadPreview from "../../../components/restaurant/ImageUploadPreview";
import { toast } from "react-toastify";
import CategoryDropdown from "../../../components/common/CategoryDropdown";


const generateSKU = () => `SKU-${Math.floor(100000 + Math.random() * 900000)}`;
const supportedLanguages = ["en", "hi"];

const MenuFormStaff = forwardRef(({ categories, inventory, onCategoryAdded }, ref) => {
  const [menu, setMenu] = useState({
    categoryId: "",
    sku: generateSKU(),
    mrp: "",
    price: "",
    quantity: "",
    hasTax: 0,
    tax: "",
    inventory_manage: 0,
    ingredients: [],
    images: [],
    uploads: [],
    activeLang: "en",
    has_variation: 0,
    translations: supportedLanguages.reduce((acc, lang) => {
      acc[lang] = { title: "", shortDescription: "", description: "" };
      return acc;
    }, {}),
  });

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryActionLoading, setCategoryActionLoading] = useState(false);
  const [newCategory, setNewCategory] = useState({
    translations: supportedLanguages.reduce((acc, lang) => {
      acc[lang] = { name: "", description: "" };
      return acc;
    }, {}),
    activeLang: "en"
  });
  const [isRefreshingCategories, setIsRefreshingCategories] = useState(false);

  const handleCreateCategory = async () => {
    if (!newCategory.translations.en.name.trim()) {
      toast.error("Category name in English is required");
      return;
    }

    try {
      setCategoryActionLoading(true);
      const categoryData = [
        {
          en: newCategory.translations.en,
          hi: newCategory.translations.hi
        }
      ];
      const res = await api.post("restaurant-staff/subcategory/request", categoryData);
      if (res) {
        toast.success("Category added successfully");
        setShowCategoryModal(false);
        setNewCategory({
          translations: supportedLanguages.reduce((acc, lang) => {
            acc[lang] = { name: "", description: "" };
            return acc;
          }, {}),
          activeLang: "en"
        });
        if (onCategoryAdded) onCategoryAdded();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to create category");
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const updateField = (field, value) =>
    setMenu((prev) => ({ ...prev, [field]: value }));

  const updateTranslation = (lang, field, value) => {
    setMenu((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [field]: value },
      },
    }));
  };

  const toggleField = (field) => {
    setMenu((prev) => ({ ...prev, [field]: prev[field] === 1 ? 0 : 1 }));
  };

  const addIngredient = () => {
    setMenu((prev) => ({
      ...prev,
      ingredients: [
        ...prev.ingredients,
        { id: null, inventoryId: "", measurement: "", reduceStock: "" },
      ],
    }));
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...menu.ingredients];
    updated[index][field] = value;
    setMenu((prev) => ({ ...prev, ingredients: updated }));
  };

  const removeIngredient = (index) => {
    setMenu((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const formData = new FormData();
    files.forEach((file) => formData.append("images[]", file));

    try {
      const res = await api.post(
        "restaurant-staff/restaurant/menu/upload-images",
        formData
      );
      const uploaded = res.data.images;
      setMenu((prev) => ({
        ...prev,
        images: [...prev.images, ...uploaded],
      }));
    } catch (err) {
      console.error("Upload error:", err);
    }
  };

  // === Expose methods to parent ===
  useImperativeHandle(ref, () => ({
    getSanitizedData: () => {
      return {
        ...menu,
        sku: menu.sku || generateSKU(),
        categoryId: menu.categoryId ? Number(menu.categoryId) : null,
        mrp: menu.mrp ? Number(menu.mrp) : 0,
        price: menu.price ? Number(menu.price) : 0,
        quantity: menu.quantity ? Number(menu.quantity) : 0,
        hasTax: Number(menu.hasTax),
        tax: menu.hasTax ? (menu.tax ? Number(menu.tax) : 0) : 0,
        inventory_manage: Number(menu.inventory_manage),
        ingredients:
          menu.inventory_manage === 1
            ? menu.ingredients
                .filter((ing) => ing.inventoryId && ing.inventoryId !== "")
                .map((ing) => ({
                  id: ing.id || null,
                  inventoryId: ing.inventoryId,
                  measurement: empty(ing.measurement) ? null : ing.measurement,
                  reduceStock: ing.reduceStock ? Number(ing.reduceStock) : 0,
                }))
            : [],
        translations: menu.translations,
        images: menu.images,
      };
    },
    resetForm: () => {
      setMenu({
        categoryId: "",
        sku: "",
        mrp: "",
        price: "",
        quantity: "",
        hasTax: 0,
        tax: "",
        inventory_manage: 0,
        ingredients: [],
        images: [],
        uploads: [],
        activeLang: "en",
        has_variation: 0,
        translations: supportedLanguages.reduce((acc, lang) => {
          acc[lang] = { title: "", shortDescription: "", description: "" };
          return acc;
        }, {}),
      });
    },
  }));

  return (
    <div className="bg-white rounded-lg">
      {/* === Basic Info === */}
      <section className="mb-10">
        {/* <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Basic Information
        </h3> */}

        {/* Language Tabs */}
        <div className="flex gap-2 mb-4">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => updateField("activeLang", lang)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium border transition ${
                menu.activeLang === lang
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Translation Fields */}
        <div className="space-y-4">
          {[{ label: "Item Name", field: "title" },{ label:"shortDescription", field: "shortDescription" },{ label:"description", field: "description" }].map(({field, label }) => {
            const Component = field === "description" ? "textarea" : "input";
            label =
              label === "shortDescription"
                ? "Short Description"
                : label.charAt(0).toUpperCase() + label.slice(1);
            return (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label} ({menu.activeLang.toUpperCase()})
                </label>
                <Component
                  rows={field === "description" ? 4 : undefined}
                  value={menu.translations[menu.activeLang][field]}
                  onChange={(e) =>
                    updateTranslation(menu.activeLang, field, e.target.value)
                  }
                  placeholder={`${label} (${menu.activeLang})`}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            );
          })}
        </div>

        {/* SKU + Category */}
        <div className="w-full mt-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* SKU BLOCK */}
            <div className="md:w-2/5 w-full">
              <label className="text-sm font-medium text-gray-700">SKU</label>
              <input
                type="text"
                value={menu.sku}
                onChange={(e) => updateField("sku", e.target.value)}
                className="w-full border border-gray-300 bg-white rounded-lg px-3 h-[42px] mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                placeholder="Enter SKU"
              />
            </div>

            {/* GENERATE BUTTON */}
            
              <button
                type="button"
                onClick={() => updateField("sku", generateSKU())}
                className="mb-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 h-[42px] rounded-lg mt-6 md:mt-6 text-sm"
              >
                Generate
              </button>
          

            {/* CATEGORY BLOCK */}
            <div className="md:w-2/5 w-full mb-2 flex gap-2 items-end">
              <div className="w-full">
                <label className="text-sm font-medium text-gray-700">Category</label>
                <CategoryDropdown
                  value={menu.categoryId}
                  onChange={(val) => updateField("categoryId", val)}
                  categories={categories}
                  placeholder="Select Category"
                />
              </div>

              {/* REFRESH BUTTON */}
              <button
                type="button"
                onClick={async () => { 
                  if (onCategoryAdded) {
                    setIsRefreshingCategories(true);
                    try {
                      await onCategoryAdded();
                    } finally {
                      setIsRefreshingCategories(false);
                    }
                  } 
                }}
                disabled={isRefreshingCategories}
                className="h-[42px] w-[42px] flex-shrink-0 mt-6 md:mt-6 bg-gray-100 hover:bg-gray-200 border rounded-lg text-gray-700 flex items-center justify-center disabled:opacity-50"
                title="Refresh Categories"
              >
                <FaSync className={isRefreshingCategories ? "animate-spin" : ""} />
              </button>

              {/* PLUS BUTTON */}
              <button
                type="button"
                onClick={() => setShowCategoryModal(true)}
                className="h-[42px] w-[42px] flex-shrink-0 mt-6 md:mt-6 bg-blue-600 hover:bg-blue-700 border rounded-lg text-white flex items-center justify-center"
                title="Add Category"
              >
                <FaPlus />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* === Pricing & Inventory === */}
      <section className="mb-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Pricing & Inventory
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {["mrp", "price", "quantity"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                {field}
              </label>
              <input
                type="number"
                min="0"
                value={menu[field]}
                onChange={(e) => updateField(field, e.target.value)}
                onWheel={(e) => e.target.blur()}
                className="w-full border px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Has Tax */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Include Tax
            </span>
            <button
              type="button"
              onClick={() => toggleField("hasTax")}
              className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${
                menu.hasTax ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                  menu.hasTax ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Inventory Manage */}
          <div className="flex items-start justify-end gap-2">
            <span className="text-sm font-medium text-gray-700">
              Inventory Manage
            </span>
            <button
              type="button"
              onClick={() => toggleField("inventory_manage")}
              className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${
                menu.inventory_manage ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                  menu.inventory_manage ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {menu.hasTax === 1 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax %
            </label>
            <input
              type="number"
              min="0"
              value={menu.tax}
              onChange={(e) => updateField("tax", e.target.value)}
              className=" border px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </section>

      {/* === Ingredients === */}
      {menu.inventory_manage === 1 && (
        <section className="mb-10">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Ingredients
          </h3>

          {menu.ingredients.length > 0 && (
            <div className="hidden sm:grid grid-cols-4 gap-4 px-2 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded-t-md">
              <div>Ingredient</div>
              <div>Measurement</div>
              <div>Reduce Stock</div>
              <div>Action</div>
            </div>
          )}

          {menu.ingredients.map((ing, i) => {
            // Collect all selected ingredient IDs except the current one
            const selectedIds = menu.ingredients
              .map((ingr, idx) => (idx !== i ? ingr.inventoryId : null))
              .filter(Boolean);

            return (
              <div
                key={i}
                className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center py-2 border-b"
              >
                {/* === Ingredient Select (with disable logic) === */}
                <select
                  value={ing.inventoryId || ""}
                  onChange={(e) => updateIngredient(i, "inventoryId", e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Ingredient</option>
                  {inventory.map((item) => (
                    <option
                      key={item.id}
                      value={item.id}
                      disabled={selectedIds.includes(String(item.id))}
                    >
                      {item.itemName} ({item.measurement})
                    </option>
                  ))}
                </select>

                {/* Measurement */}
                <select
                  value={ing.measurement}
                  onChange={(e) =>
                    updateIngredient(i, "measurement", e.target.value)
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Measurement</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                  <option value="piece">piece</option>
                </select>

                {/* Reduce Stock */}
                <input
                  type="number"
                  min="0"
                  placeholder="Reduce Stock"
                  value={ing.reduceStock}
                  onChange={(e) =>
                    updateIngredient(i, "reduceStock", e.target.value)
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeIngredient(i)}
                  className="text-red-600 hover:text-red-800 flex justify-start sm:justify-center"
                  title="Remove Ingredient"
                >
                  <FaTrashAlt size={18} />
                </button>
              </div>
            );
          })}

          <button
            type="button"
            onClick={addIngredient}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-md"
          >
            + Add Ingredient
          </button>
        </section>
      )}

      {/* === Image Upload === */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Images</h3>
        <ImageUploadPreview
          initialImages={menu?.uploads}
          onChange={(imgs) => updateField("images", imgs)}
          onUpload={handleFileUpload}
        />
      </section>
      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Add Category</h2>
            
            <div className="flex gap-2 mb-4">
              {supportedLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() =>
                    setNewCategory((prev) => ({ ...prev, activeLang: lang }))
                  }
                  className={`px-3 py-1 text-sm rounded-md border ${
                    newCategory.activeLang === lang
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Category Name ({newCategory.activeLang.toUpperCase()}) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newCategory.translations[newCategory.activeLang].name}
                onChange={(e) =>
                  setNewCategory((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [newCategory.activeLang]: {
                        ...prev.translations[newCategory.activeLang],
                        name: e.target.value,
                      },
                    },
                  }))
                }
                placeholder={`Enter category name (${newCategory.activeLang})`}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Description ({newCategory.activeLang.toUpperCase()}) <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <textarea
                rows={3}
                value={newCategory.translations[newCategory.activeLang].description}
                onChange={(e) =>
                  setNewCategory((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [newCategory.activeLang]: {
                        ...prev.translations[newCategory.activeLang],
                        description: e.target.value,
                      },
                    },
                  }))
                }
                placeholder={`Enter description (${newCategory.activeLang}) (optional)`}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={categoryActionLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
              >
                {categoryActionLoading ? "Saving..." : "Add Category"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
});

export default MenuFormStaff;
